import React from 'react';

/**
 * Minimal, dependency-free Markdown renderer for chat bubbles.
 *
 * Supports the subset the Prompt-Architekt actually emits: headings (#..###),
 * bullet/numbered lists, bold (**x**), inline code (`x`), fenced code blocks
 * (```...```) and paragraphs. Intentionally not a full CommonMark parser — it
 * keeps the bundle small and avoids a new npm dependency.
 */

type InlineToken = { type: 'text' | 'bold' | 'code'; value: string };

const parseInline = (text: string): InlineToken[] => {
  const tokens: InlineToken[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    if (m[2] !== undefined) {
      tokens.push({ type: 'bold', value: m[2] });
    } else if (m[3] !== undefined) {
      tokens.push({ type: 'code', value: m[3] });
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return tokens;
};

/** Split a Markdown table row into trimmed cells, ignoring outer pipes. */
const splitRow = (row: string): string[] =>
  row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(c => c.trim());

/** A separator row looks like `| --- | :--: | ---: |`. */
const isTableSeparator = (row: string): boolean =>
  /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(row);

const renderInline = (text: string, keyPrefix: string): React.ReactNode[] =>
  parseInline(text).map((tok, i) => {
    const key = `${keyPrefix}-${i}`;
    if (tok.type === 'bold') {
      return (
        <strong key={key} className="font-semibold">
          {tok.value}
        </strong>
      );
    }
    if (tok.type === 'code') {
      return (
        <code
          key={key}
          className="rounded bg-gray-200 dark:bg-gray-800 px-1 py-0.5 text-[0.85em] font-mono"
        >
          {tok.value}
        </code>
      );
    }
    return <React.Fragment key={key}>{tok.value}</React.Fragment>;
  });

export const SimpleMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];

  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line.trim())) {
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      blocks.push(
        <pre
          key={`b-${key++}`}
          className="my-2 overflow-x-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100"
        >
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Table: a `|`-row followed by a separator row (| --- | --- |).
    if (
      line.includes('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const headerCells = splitRow(line);
      i += 2; // skip header + separator
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        bodyRows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push(
        <div key={`b-${key++}`} className="my-2 overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                {headerCells.map((cell, ci) => (
                  <th
                    key={`th-${ci}`}
                    className="border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-1 text-left font-semibold"
                  >
                    {renderInline(cell, `th-${key}-${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={`tr-${ri}`}>
                  {headerCells.map((_, ci) => (
                    <td
                      key={`td-${ri}-${ci}`}
                      className="border border-gray-300 dark:border-gray-600 px-2 py-1 align-top"
                    >
                      {renderInline(row[ci] ?? '', `td-${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      const content = renderInline(heading[2], `h-${key}`);
      const cls =
        level === 1
          ? 'mt-3 mb-1 text-base font-bold'
          : level === 2
            ? 'mt-3 mb-1 text-sm font-bold'
            : 'mt-2 mb-1 text-sm font-semibold';
      blocks.push(
        <div key={`b-${key++}`} className={cls}>
          {content}
        </div>
      );
      i += 1;
      continue;
    }

    // List (bullet or numbered) — collect consecutive items
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: React.ReactNode[] = [];
      let itemKey = 0;
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        const itemText = lines[i].replace(/^\s*([-*]|\d+\.)\s+/, '');
        items.push(
          <li key={`li-${itemKey++}`} className="ml-1">
            {renderInline(itemText, `li-${key}-${itemKey}`)}
          </li>
        );
        i += 1;
      }
      blocks.push(
        ordered ? (
          <ol key={`b-${key++}`} className="my-1 ml-5 list-decimal space-y-0.5">
            {items}
          </ol>
        ) : (
          <ul key={`b-${key++}`} className="my-1 ml-5 list-disc space-y-0.5">
            {items}
          </ul>
        )
      );
      continue;
    }

    // Blank line
    if (line.trim() === '') {
      i += 1;
      continue;
    }

    // Paragraph — merge consecutive non-empty, non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^```/.test(lines[i].trim()) &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !/^\s*([-*]|\d+\.)\s+/.test(lines[i]) &&
      !(
        lines[i].includes('|') &&
        i + 1 < lines.length &&
        isTableSeparator(lines[i + 1])
      )
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={`b-${key++}`} className="my-1 leading-relaxed">
        {renderInline(paraLines.join(' '), `p-${key}`)}
      </p>
    );
  }

  return <div className="space-y-0.5">{blocks}</div>;
};

export default SimpleMarkdown;
